import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { BasilChevronDown, BasilSlidersHorizontal } from "@/components/icons/basil";
import { cn } from "@shared/lib/utils";
import { AssistantFilterChips } from "@/components/collection/AssistantFilterChips";
import { AssistantSection, type CollectionSort } from "@/components/collection/AssistantSection";
import type { CardSelection } from "@/components/collection/FinishCardTile";
import { supplyLine } from "@/components/collection/supply";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { GlowButton } from "@/components/ui/GlowButton";
import { Skeleton } from "@/components/ui/Skeleton";
import type { AgentCardItem, AgentCardsState } from "@/lib/api";
import { cardArt2xSrc } from "@/lib/cardArt";
import { CARD_FINISHES } from "@/lib/cards";
import { HOME_AGENTS } from "@/lib/homeAgents";

// framer-motion만 쓰는 유일한 eager 체인이 이 팝오버였다 — lazy로 떼어내
// 허브 초기 청크에서 motion 번들(~40 kB gzip)을 제외한다. open=false 동안엔
// 빈 AnimatePresence 포털만 렌더하므로 null fallback과 구분 불가(동작 동일).
const HoloCardPopover = lazy(() =>
  import("@shared/components/HoloCardPopover").then((m) => ({ default: m.HoloCardPopover })),
);

/** Loading placeholder — two ghost assistant shelves with 8 card slots each. */
function CollectionSkeleton() {
  return (
    <div className="flex flex-col gap-10" aria-hidden>
      {[0, 1].map((row) => (
        <div key={row} className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Skeleton className="size-11 rounded-full" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-20 rounded" />
              <Skeleton className="h-2.5 w-14 rounded" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {CARD_FINISHES.map((meta) => (
              <Skeleton key={meta.finish} className="aspect-[723/1024] w-full rounded-2xl" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

type CollectionGridProps = {
  authenticated: boolean;
  loading: boolean;
  error: string | null;
  state: AgentCardsState | null;
  onSignIn: () => void;
  onRetry: () => void;
};

/**
 * The collection gallery body — filter chips, per-assistant card shelves,
 * empty/error/signed-out states, and the shared holo popover. Extracted from
 * CollectionPage so /profile's Collection tab can mount the identical body
 * independently (own filter/selection state — the two mounts don't share it).
 */
export function CollectionGrid({ authenticated, loading, error, state, onSignIn, onRetry }: CollectionGridProps) {
  const [filter, setFilter] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<CollectionSort>("catalog");
  const [selection, setSelection] = useState<CardSelection | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleSelect = useCallback((next: CardSelection) => {
    setSelection(next);
    setPopoverOpen(true);
  }, []);

  const closePopover = useCallback(() => setPopoverOpen(false), []);

  const itemsByAgent = useMemo(() => {
    const map = new Map<string, AgentCardItem[]>();
    for (const item of state?.items ?? []) {
      const list = map.get(item.agentId);
      if (list) list.push(item);
      else map.set(item.agentId, [item]);
    }
    return map;
  }, [state]);

  const visibleAgents = filter
    ? HOME_AGENTS.filter((agent) => agent.agentId === filter)
    : HOME_AGENTS;

  const popoverMeta = selection
    ? `${supplyLine(selection.meta, selection.item)}${selection.item.count > 1 ? ` · ×${selection.item.count} owned` : ""}`
    : undefined;

  return (
    <>
      {loading ? (
        <CollectionSkeleton />
      ) : !authenticated ? (
        <GlassPanel className="flex flex-col items-center gap-2.5 px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-xs text-ink-dim">
            Sign in to reveal your card collection — every assistant, every finish.
          </p>
          <GlowButton onClick={onSignIn} className="shrink-0">
            Sign in with Telegram
          </GlowButton>
        </GlassPanel>
      ) : error ? (
        <GlassPanel className="flex flex-col items-center gap-3 px-6 py-8 text-center">
          <p className="text-xs text-ink-dim">Couldn't load your collection.</p>
          <p className="max-w-sm font-mono text-[10px] text-white/55">{error}</p>
          <GlowButton variant="ghost" onClick={onRetry}>
            Retry
          </GlowButton>
        </GlassPanel>
      ) : (
        <>
          {state && state.ownedCount === 0 ? (
            <GlassPanel className="flex flex-col items-center gap-2.5 px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
              <p className="text-xs text-ink-dim">
                You haven't pulled a card yet — open your first pack to get started.
              </p>
              <GlowButton to="/shop" className="shrink-0">
                Pull your first card
              </GlowButton>
            </GlassPanel>
          ) : null}

          {/* Courtyard toolbar (§3) — one sticky line: [n Items | sort select | Filters].
              View toggle + search omitted (no alternate view / search yet); order preserved.
              Doubles as the §2C collection-rate header pinned atop the grid. */}
          <div className="sticky top-14 z-30 -mx-1 flex items-center justify-between gap-3 border-b border-line bg-(--ty-bg) px-1 py-2 lg:top-16">
            <p className="font-mono text-xs font-medium tabular-nums text-ink">
              {state?.ownedCount ?? 0}{" "}
              <span className="font-normal text-ink-mute">/ {state?.totalCount ?? 0} Items</span>
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <label className="relative block">
                <span className="sr-only">Sort order</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as CollectionSort)}
                  className="min-h-11 cursor-pointer appearance-none rounded-lg border border-line bg-white/4 pl-3 pr-8 font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70"
                >
                  <option value="catalog">Catalog №</option>
                  <option value="owned">Owned first</option>
                </select>
                <BasilChevronDown
                  className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-mute"
                  aria-hidden
                />
              </label>
              <button
                type="button"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((o) => !o)}
                className={cn(
                  "flex min-h-11 items-center gap-1.5 rounded-lg border px-3 font-mono text-[11px] font-medium uppercase tracking-[0.08em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70",
                  filtersOpen || filter
                    ? "border-accent/60 bg-accent/10 text-ink"
                    : "border-line bg-white/4 text-ink-dim hover:border-line-hi hover:text-ink",
                )}
              >
                <BasilSlidersHorizontal className="size-3.5" aria-hidden />
                Filters
                {filter ? <span className="tabular-nums text-ink-mute">· 1</span> : null}
              </button>
            </div>
          </div>

          {filtersOpen ? <AssistantFilterChips selected={filter} onSelect={setFilter} /> : null}

          <div className="flex flex-col gap-10">
            {visibleAgents.map((agent) => (
              <AssistantSection
                key={agent.agentId}
                agent={agent}
                items={itemsByAgent.get(agent.agentId) ?? []}
                numberBase={HOME_AGENTS.indexOf(agent) * CARD_FINISHES.length}
                sort={sort}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </>
      )}

      {/* Single shared popover — full holo effect, originRect fly-in from the clicked tile. */}
      <Suspense fallback={null}>
        <HoloCardPopover
          open={popoverOpen}
          onClose={closePopover}
          // 확대 뷰 = 960w(@2x) 실카드 아트 우선, 미존재 조합은 기존 초상 폴백
          imageSrc={
            selection
              ? (cardArt2xSrc(selection.agent.agentId, selection.meta.finish) ??
                selection.agent.portrait)
              : ""
          }
          imageAlt={selection ? `${selection.agent.name} — ${selection.meta.label}` : ""}
          effect={selection?.meta.holoEffect ?? "basic"}
          originRect={selection?.originRect ?? null}
          subtitle={selection ? `${selection.agent.name} · ${selection.meta.label}` : undefined}
          meta={popoverMeta}
        />
      </Suspense>
    </>
  );
}
